import React, {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from "react";
import { inject, observer } from "mobx-react";
import NodeChatStore from "../_stores/nodechat/NodeChatStore";
import BroadcastStore from "../_stores/broadcast/BroadcastStore";
import ChatStore from "../_stores/chat/ChatStore";
import { IChatMessage, IChatNotification } from "../_stores/chat/interfaces";
import { ChatState, ChatModeEnum } from "../_stores/chat/enums";
import { BroadcastStreamState } from "../_stores/broadcast/enums";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindBox from "library/components/_tailwind/box";
import TailwindText from "library/components/_tailwind/text";
import TailwindIcon from "library/components/_tailwind/icon";
import CustomIcon from "library/components/_tailwind/icon/icons";
import { useIntl } from "react-intl";
import { BROADCAST_CHAT_INPUT_MAX_CHARS } from "../broadcast-chat/chat-tab";
import EmojiPopover from "../broadcast-chat/chat-emoji-popover";
import TranslationPopover from "../broadcast-chat/chat-translation-popover";
import BroadcastChatFontSizePopover from "../broadcast-chat/chat-font-size-popover";
import ProfileStore from "common/my-page/stores/profile/ProfileStore";
import { TwitterMessage } from "../_stores/broadcast/consts";
import config from "core/config";
import { languageStore } from "library/core/stores/language/LanguageStore";
import { resolveNotificationParams } from "../_stores/chat/notification-helpers";
import "./styles.scss";

const MAX_OVERLAY_MESSAGES = 200;

const MAX_VISIBLE_OVERLAY_MESSAGES = 4;

const SCROLL_AT_BOTTOM_THRESHOLD_PX = 40;

const DEFAULT_OVERLAY_FONT_SIZE = 14;

// Window after a programmatic scroll during which native scroll events are
// ignored. Prevents the smooth-scroll animation from being interpreted as a
// user-initiated scroll-up and re-toggling the "new messages" pill.
const PROGRAMMATIC_SCROLL_WINDOW_MS = 500;

type Props = {
  nodeChatStore?: NodeChatStore;
  broadcastStore?: BroadcastStore;
  chatStore?: ChatStore;
  profileStore?: ProfileStore;
  visible: boolean;
};

const OverlayMessage: React.FC<{
  message: IChatMessage;
  opacity: number;
  fontSize: number;
}> = observer(({ message, opacity, fontSize }) => {
  // Read intl from the MobX-observable languageStore so the overlay
  // re-renders the moment the model toggles language.
  const { intl } = languageStore;

  const cleanUsername = useMemo(() => {
    if (!message.from) return "";
    if (message.from.includes("ffadult") || message.from.includes("bdsm")) {
      return message.from.split(/_/)[0];
    }
    return message.from.split(/\+|@/)[0];
  }, [message?.from]);

  const cleanText = useMemo(() => {
    if (message?.text?.includes("(auto-translated)")) {
      return message.text.replace("(auto-translated)", "");
    }
    return message.text;
  }, [message?.text]);

  const displayName = message.isModel
    ? intl.formatMessage({
        id: "broadcast.chatItem.me",
        defaultMessage: "Me",
      })
    : cleanUsername || message.from;

  if (message.type === "tip") {
    return (
      <TailwindBox
        padding={["px-3", "py-1.5"]}
        margin={["mb-1.5"]}
        borderRadius='rounded-2xl'
        style={{
          background: "linear-gradient(90deg, rgba(0,255,55,0.7) 0%, rgba(166,255,0,0.5) 100%)",
          backdropFilter: "blur(4px)",
          maxWidth: "85%",
          opacity,
          transition: "opacity 0.3s ease",
        }}>
        <TailwindText style={{ color: "#000", fontSize }}>
          <TailwindText
            as='span'
            fontWeight='font-bold'
            style={{ color: "#000", fontSize }}>
            {displayName}
          </TailwindText>{" "}
          {intl.formatMessage(
            {
              id: "broadcast.overlay.tipped",
              defaultMessage: "tipped {value} tokens",
            },
            { value: message.value }
          )}
        </TailwindText>
      </TailwindBox>
    );
  }

  if (message.type === "gift") {
    return (
      <TailwindBox
        padding={["px-3", "py-1.5"]}
        margin={["mb-1.5"]}
        borderRadius='rounded-2xl'
        style={{
          background: "linear-gradient(90deg, rgba(42,189,118,0.7) 0%, rgba(18,74,135,0.5) 100%)",
          backdropFilter: "blur(4px)",
          maxWidth: "85%",
          opacity,
          transition: "opacity 0.3s ease",
        }}>
        <TailwindText style={{ color: "#fff", fontSize }}>
          <TailwindText
            as='span'
            fontWeight='font-bold'
            style={{ color: "#fff", fontSize }}>
            {displayName}
          </TailwindText>{" "}
          {intl.formatMessage({
            id: "broadcast.overlay.sentGift",
            defaultMessage: "sent a gift",
          })}
        </TailwindText>
      </TailwindBox>
    );
  }

  return (
    <TailwindBox
      padding={["px-3", "py-1.5"]}
      margin={["mb-1.5"]}
      borderRadius='rounded-2xl'
      style={{
        background: "rgba(0, 0, 0, 0.4)",
        backdropFilter: "blur(4px)",
        maxWidth: "85%",
        opacity,
        transition: "opacity 0.3s ease",
      }}>
      <TailwindText textColor='text-white' style={{ fontSize }}>
        <TailwindText
          as='span'
          fontWeight='font-bold'
          style={{
            color: message.isModel
              ? "#ffb347"
              : message.isAdmin
              ? "#ff4444"
              : "#f0abfc",
            fontSize,
          }}>
          {message.isAdmin &&
            intl.formatMessage({
              id: "broadcast.overlay.adminPrefix",
              defaultMessage: "[Admin] ",
            })}
          {displayName}:
        </TailwindText>{" "}
        {cleanText}
      </TailwindText>
    </TailwindBox>
  );
});

const OverlayNotification: React.FC<{
  notification: IChatNotification;
  opacity: number;
  fontSize: number;
}> = observer(({ notification, opacity, fontSize }) => {
  // Read intl from the MobX-observable languageStore so the overlay
  // re-renders the moment the model toggles language.
  const { intl } = languageStore;

  const text = notification.fixedContentKey
    ? intl.formatMessage(
        {
          id: notification.fixedContentKey,
          defaultMessage: notification.content,
        },
        resolveNotificationParams(notification, intl)
      )
    : notification.content || "";

  if (!text) return null;

  return (
    <TailwindBox
      padding={["px-3", "py-1.5"]}
      margin={["mb-1.5"]}
      borderRadius='rounded-2xl'
      style={{
        background: "rgba(255, 255, 255, 0.12)",
        backdropFilter: "blur(4px)",
        maxWidth: "85%",
        opacity,
        transition: "opacity 0.3s ease",
      }}>
      <TailwindText style={{ color: "rgba(255, 255, 255, 0.8)", fontSize }}>
        {intl.formatMessage({
          id: "broadcast.overlay.systemPrefix",
          defaultMessage: "[System]: ",
        })}
        {text.replace(/<[^>]*>/g, "")}
      </TailwindText>
    </TailwindBox>
  );
});

const glassIconStyle = {
  width: 28,
  height: 28,
  background: "rgba(255, 255, 255, 0.12)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.18)",
};

const MobileBroadcastOverlayChat: React.ComponentType<Props> = ({
  nodeChatStore,
  broadcastStore,
  chatStore,
  profileStore,
  visible,
}) => {
  const {
    publicChatMessagesAndNotifications,
    privateChatMessagesAndNotifications,
    chatState,
    sendMessage,
  } = nodeChatStore!;
  const {
    isShowStarted,
    isInPaidShow,
    streamState,
    isLiveTranslationActive,
    isPushToTalkActive,
    isPushToTalkCoolingDown,
    startPushToTalk,
    stopPushToTalk,
  } = broadcastStore!;
  const {
    message,
    setMessage,
    chatFontSize,
    setChatFontSize,
  } = chatStore!;
  const { currentChatLanguage } = nodeChatStore!;
  const { profile } = profileStore!;
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const intl = useIntl();

  const isChatReady = chatState === ChatState.started;
  const canSend = streamState === BroadcastStreamState.started;

  const activeChatMessagesAndNotifications = isInPaidShow
    ? privateChatMessagesAndNotifications
    : publicChatMessagesAndNotifications;

  const recentMessages = useMemo(() => {
    if (!activeChatMessagesAndNotifications) return [];
    return activeChatMessagesAndNotifications
      .filter(msg => {
        if (msg.isMessageOrNotification === "message" && msg.text) {
          return !msg.text.trim().startsWith("[Live Translation Audio]");
        }
        return true;
      })
      .slice(-MAX_OVERLAY_MESSAGES);
  }, [activeChatMessagesAndNotifications]);

  const [isAtBottom, setIsAtBottom] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  // Mirror of `isAtBottom` accessible from the auto-scroll effect without
  // forcing it to depend on the state -- depending on it would interrupt
  // the smooth "jump to latest" animation as soon as the state flips.
  const isAtBottomRef = useRef(true);
  const lastSeenCountRef = useRef(0);
  const programmaticScrollUntilRef = useRef(0);

  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = scrollRef.current;
    if (!el) return;
    if (smooth && typeof el.scrollTo === "function") {
      // Only smooth scrolls fire scroll events over time; protect those
      // from being mis-interpreted as a user scroll-up while animating.
      programmaticScrollUntilRef.current =
        Date.now() + PROGRAMMATIC_SCROLL_WINDOW_MS;
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    // Ignore scroll events fired by our own programmatic scrollTo so the
    // smooth animation isn't mis-interpreted as a user scroll-up.
    if (Date.now() < programmaticScrollUntilRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const atBottom = distanceFromBottom < SCROLL_AT_BOTTOM_THRESHOLD_PX;
    if (atBottom !== isAtBottomRef.current) {
      isAtBottomRef.current = atBottom;
      setIsAtBottom(atBottom);
    }
    if (atBottom) {
      setUnreadCount(prev => (prev === 0 ? prev : 0));
    }
  }, []);

  // Mirrors the original behavior: whenever the message list changes and
  // the model is at the bottom, snap to the newest message. When the
  // model has scrolled up, we instead track how many new
  // messages/buzz/gifts/tips arrived so the "new messages" pill can
  // surface them without yanking the scroll position.
  useEffect(() => {
    const count = recentMessages.length;
    if (isAtBottomRef.current) {
      scrollToBottom(false);
      lastSeenCountRef.current = count;
      setUnreadCount(prev => (prev === 0 ? prev : 0));
    } else {
      const delta = count - lastSeenCountRef.current;
      const next = delta > 0 ? delta : 0;
      setUnreadCount(prev => (prev === next ? prev : next));
    }
  }, [recentMessages, scrollToBottom]);

  const handleJumpToLatest = useCallback(() => {
    isAtBottomRef.current = true;
    setIsAtBottom(true);
    lastSeenCountRef.current = recentMessages.length;
    setUnreadCount(0);
    scrollToBottom(true);
  }, [recentMessages.length, scrollToBottom]);

  const handleSend = useCallback(() => {
    if (!message.trim()) return;
    const messageToSend = message.substring(
      0,
      BROADCAST_CHAT_INPUT_MAX_CHARS - 1
    );
    sendMessage(
      messageToSend,
      isInPaidShow ? ChatModeEnum.PRIVATE : ChatModeEnum.PUBLIC
    );
    setMessage("");
    inputRef.current?.focus();
  }, [message, sendMessage, setMessage, isInPaidShow]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleShare = useCallback(() => {
    const shareUrl = `${config.membersUrl}/reg/${profile.username}`;
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(TwitterMessage)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(tweetUrl, "_blank", "noopener,noreferrer");
  }, [profile.username]);

  const addEmoji = useCallback(
    (emoji: string) => {
      const el = inputRef.current;
      if (!el) return;
      const start = el.selectionStart ?? message.length;
      const newMessage =
        message.substring(0, start) + emoji + message.substring(start);
      setMessage(newMessage);
      el.focus();
    },
    [message, setMessage]
  );

  const overlayFontSize = chatFontSize || DEFAULT_OVERLAY_FONT_SIZE;

  const onSetFont = useCallback(
    (font: number | string) => {
      if (typeof font === "number") {
        setChatFontSize(font);
      }
    },
    [setChatFontSize]
  );

  if (!visible || !isShowStarted || !isChatReady) return null;

  return (
    <TailwindFlex
      position='absolute'
      inset={["bottom-16", "left-0", "right-0"]}
      flexDirection='flex-col'
      justifyContent='justify-end'
      zIndex='z-10'
      style={{
        pointerEvents: "none",
      }}>
      {/* Scrollable message list -- compact height of ~4 messages with a
          top fade. When the model scrolls up to review older messages we
          drop the fade and pause auto-scroll, then show a "new messages"
          pill so they can jump back to the live view on demand. */}
      <TailwindFlex
        ref={scrollRef}
        flexDirection='flex-col'
        padding={["px-3", "pb-2"]}
        width='w-3/4'
        overflow={["overflow-y-auto", "overflow-x-hidden"]}
        htmlElementProps={{ onScroll: handleScroll }}
        className={["mobile-overlay-chat-scroll"]}
        style={{
          pointerEvents: "auto",
          maxHeight: `${Math.round(
            (overlayFontSize * 1.4 + 18) * MAX_VISIBLE_OVERLAY_MESSAGES
          )}px`,
          overscrollBehavior: "contain",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
          WebkitMaskImage: isAtBottom
            ? "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.5) 25%, black 60%, black 100%)"
            : undefined,
          maskImage: isAtBottom
            ? "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.5) 25%, black 60%, black 100%)"
            : undefined,
        }}>
        {/* Spacer pushes messages to the bottom when content is short.
            We deliberately avoid `justify-content: flex-end` on the scroll
            container -- in flex column + overflow-auto, justify-end places
            overflowing content above the visible area where it cannot be
            reached by user scroll on Chrome / Safari / iOS. */}
        <div style={{ marginTop: "auto" }} />
        {recentMessages.map((msg, index) => {
          if (msg.isMessageOrNotification === "message") {
            return (
              <OverlayMessage
                key={`overlay-msg-${index}`}
                message={msg}
                opacity={1}
                fontSize={overlayFontSize}
              />
            );
          }
          return (
            <OverlayNotification
              key={`overlay-notif-${index}`}
              notification={msg}
              opacity={1}
              fontSize={overlayFontSize}
            />
          );
        })}
      </TailwindFlex>

      {/* "New messages" pill -- only visible when the model has scrolled
          up to review older messages and new chat/buzz/gift/tip activity
          has arrived since. Tapping smoothly returns to the live view. */}
      {!isAtBottom && (
        <TailwindFlex
          width='w-3/4'
          padding={["px-3"]}
          margin={["mb-1"]}
          justifyContent='justify-center'
          style={{ pointerEvents: "auto" }}>
          <TailwindBox
            as='button'
            display='flex'
            alignItems='items-center'
            justifyContent='justify-center'
            cursor='cursor-pointer'
            borderRadius='rounded-full'
            padding={["px-3", "py-1"]}
            onClick={handleJumpToLatest}
            htmlElementProps={{
              "aria-label": intl.formatMessage({
                id: "broadcast.overlay.jumpToLatest",
                defaultMessage: "Jump to latest messages",
              }),
            }}
            style={{
              gap: 6,
              background: "rgba(0, 0, 0, 0.55)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.25)",
            }}>
            <TailwindIcon
              name='arrow_downward'
              fontSize='text-xs'
              textColor='text-white'
            />
            <TailwindText
              textColor='text-white'
              style={{ fontSize: 12, fontWeight: 600 }}>
              {unreadCount > 0
                ? intl.formatMessage(
                    {
                      id: "broadcast.overlay.newMessagesCount",
                      defaultMessage:
                        "{count, plural, one {# new message} other {# new messages}}",
                    },
                    { count: unreadCount }
                  )
                : intl.formatMessage({
                    id: "broadcast.overlay.jumpToLatest",
                    defaultMessage: "Jump to latest",
                  })}
            </TailwindText>
          </TailwindBox>
        </TailwindFlex>
      )}

      {/* Input row: [input+emoji] [translate] [mic*] [send] */}
      <TailwindFlex
        width='w-full'
        padding={["px-3", "pt-2", "pb-1"]}
        gap='gap-1'
        alignItems='items-center'
        style={{ pointerEvents: "auto" }}>
        {/* Glass input with emoji inside */}
        <TailwindFlex
          flex='flex-1'
          alignItems='items-center'
          borderRadius='rounded-full'
          padding={["pl-3", "pr-0.5"]}
          style={{
            height: 28,
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "1px solid rgba(255, 255, 255, 0.25)",
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.15)",
            minWidth: 0,
          }}>
          <input
            ref={inputRef}
            type='text'
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!canSend}
            maxLength={BROADCAST_CHAT_INPUT_MAX_CHARS}
            placeholder={intl.formatMessage({
              id: "broadcast.chatInputPlaceholder",
              defaultMessage: "Type your message",
            })}
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "#fff",
              fontSize: `${Math.min(overlayFontSize, 14)}px`,
              minWidth: 0,
              height: "100%",
            }}
          />
          <EmojiPopover onSelectEmoji={addEmoji}>
            <TailwindBox
              as='button'
              display='flex'
              alignItems='items-center'
              justifyContent='justify-center'
              flex='flex-none'
              cursor='cursor-pointer'
              padding={["p-1"]}
              style={{ opacity: 0.7 }}>
              <TailwindIcon
                name='tag_faces'
                fontSize='text-base'
                textColor='text-white'
              />
            </TailwindBox>
          </EmojiPopover>
        </TailwindFlex>

        {/* Font size (Aa) -- opens popover with full size options */}
        <BroadcastChatFontSizePopover
          fontSize={overlayFontSize}
          onSetFont={onSetFont}>
          <TailwindBox
            as='button'
            display='flex'
            alignItems='items-center'
            justifyContent='justify-center'
            borderRadius='rounded-full'
            flex='flex-none'
            cursor='cursor-pointer'
            textColor='text-white'
            style={glassIconStyle}
            htmlElementProps={{
              "aria-label": intl.formatMessage({
                id: "broadcast.overlay.fontSize",
                defaultMessage: "Change chat text size",
              }),
              title: intl.formatMessage({
                id: "broadcast.overlay.fontSize",
                defaultMessage: "Change chat text size",
              }),
            }}>
            <span
              style={{
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: "-0.5px",
              }}>
              <span style={{ fontSize: 13 }}>A</span>
              <span style={{ fontSize: 9 }}>a</span>
            </span>
          </TailwindBox>
        </BroadcastChatFontSizePopover>

        {/* Translate */}
        <TranslationPopover>
          <TailwindBox
            as='button'
            display='flex'
            alignItems='items-center'
            justifyContent='justify-center'
            borderRadius='rounded-full'
            flex='flex-none'
            textColor='text-white'
            style={glassIconStyle}>
            {currentChatLanguage !== "en" ? (
              <TailwindBox
                as='span'
                height='h-4'
                width='w-4'
                className={["fi", `fi-${currentChatLanguage}`]}
                borderRadius='rounded-full'
                backgroundSize='bg-cover'
                backgroundPosition='bg-center'
                backgroundRepeat='bg-no-repeat'
              />
            ) : (
              <CustomIcon name='translate' />
            )}
          </TailwindBox>
        </TranslationPopover>

        {/* Live Translate (mic) -- only when feature is active */}
        {isLiveTranslationActive && (
          <TailwindBox
            as='button'
            display='flex'
            alignItems='items-center'
            justifyContent='justify-center'
            borderRadius='rounded-full'
            flex='flex-none'
            textColor='text-white'
            cursor={isPushToTalkCoolingDown ? "cursor-default" : "cursor-pointer"}
            onClick={() => {
              if (isPushToTalkCoolingDown) return;
              if (isPushToTalkActive) {
                stopPushToTalk();
              } else {
                startPushToTalk();
              }
            }}
            style={{
              ...glassIconStyle,
              background: isPushToTalkActive
                ? "rgba(233,30,140,0.6)"
                : glassIconStyle.background,
            }}>
            <CustomIcon name='mic' />
          </TailwindBox>
        )}

        {/* Share broadcast */}
        {isShowStarted && (
          <TailwindBox
            as='button'
            display='flex'
            alignItems='items-center'
            justifyContent='justify-center'
            borderRadius='rounded-full'
            flex='flex-none'
            cursor='cursor-pointer'
            textColor='text-white'
            onClick={handleShare}
            style={glassIconStyle}>
            <TailwindIcon name='share' fontSize='text-sm' />
          </TailwindBox>
        )}

        {/* Send */}
        <TailwindBox
          as='button'
          display='flex'
          alignItems='items-center'
          justifyContent='justify-center'
          borderRadius='rounded-full'
          flex='flex-none'
          cursor={message.trim() ? "cursor-pointer" : "cursor-default"}
          textColor='text-white'
          onClick={handleSend}
          style={{
            ...glassIconStyle,
            background: message.trim()
              ? "var(--pink-color, #e91e8c)"
              : glassIconStyle.background,
            transition: "background 0.2s ease",
          }}>
          <TailwindIcon name='send' fontSize='text-sm' />
        </TailwindBox>
      </TailwindFlex>
    </TailwindFlex>
  );
};

export default inject(
  "nodeChatStore",
  "broadcastStore",
  "chatStore",
  "profileStore"
)(observer(MobileBroadcastOverlayChat));
